import * as React from "react";
import { CheckIcon, PlusCircleIcon } from "lucide-react";
import { Column } from "@tanstack/react-table";

import { cn, coalesce } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { GetListResponse } from "@refinedev/core";

export interface IOption {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
}
interface DataTableFacetedFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  defaultValues?: string[];
  facetKey?: string;
  data?: GetListResponse<any>;
  title?: string;
  options?: IOption[];
  shouldFilter?: boolean;
  optionAccessorFn?: (item: any) => IOption;
  searchFacet?: (query: string, columnId: string) => void;
  onClose?: () => void;
  onFilterChanged?: (value?: string) => void;
  showClearFilters?: boolean;
}

export function DataTableFacetedFilter<TData, TValue>({
  defaultValues,
  column,
  facetKey,
  data,
  title,
  options: defaultOptions,
  optionAccessorFn,
  searchFacet,
  onClose,
  onFilterChanged,
  shouldFilter = false,
  showClearFilters,
}: DataTableFacetedFilterProps<TData, TValue>) {
  // const selectedValues = new Set(defaultValues);
  const [selectedValues, setSelectedValues] = React.useState<Set<string>>(
    new Set(defaultValues),
  );
  const [query, setQuery] = React.useState<string>("");
  const [facets, setFacets] = React.useState<Map<string, any>>();
  const [options, setOptions] = React.useState<IOption[]>([]);

  React.useEffect(() => {
    const counts = data?.facets?.find(
      (x: any) => x.field_name === coalesce(facetKey, column?.id),
    )?.counts;

    setFacets(
      counts?.reduce(
        (map: any, item: any) => map.set(item.value, item.count),
        new Map(),
      ),
    );

    if (!defaultOptions) {
      setOptions((prev) => {
        const selectedOptions = prev?.filter((option) =>
          selectedValues.has(option.value),
        );

        let generatedOptions: IOption[] = [];
        if (counts && counts.length > 0) {
          generatedOptions = generatedOptions.concat(
            counts.map((facet: any) => optionAccessorFn?.(facet)),
          );
        }
        if (selectedOptions.length > 0) {
          generatedOptions = [
            ...selectedOptions.filter(
              (o) => !generatedOptions.some((g) => g.value === o.value),
            ),
            ...generatedOptions,
          ];
        }

        // if (prev.length && !generatedOptions.length) return prev;
        return generatedOptions;
      });
    }
  }, [data]);

  React.useEffect(() => {
    setSelectedValues(new Set(defaultValues));
  }, [defaultValues]);

  React.useEffect(() => {
    if (defaultOptions) setOptions(defaultOptions);
  }, [defaultOptions]);

  return (
    <Popover
      onOpenChange={(open) => !open && selectedValues.size === 0 && onClose?.()}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="border-dashed text-foreground w-full"
        >
          <PlusCircleIcon className="mr-2 h-4 w-4" />
          {title}
          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal lg:hidden"
              >
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  options
                    ?.filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={option.value}
                        className="rounded-sm px-1 font-normal"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command shouldFilter={!defaultOptions || shouldFilter}>
          <CommandInput
            placeholder={title}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>
              No results found.
              {!defaultOptions && (
                <Button
                  variant={"ghost"}
                  onClick={() => {
                    searchFacet?.(query, coalesce(facetKey, column?.id));
                    // setQuery("");
                  }}
                >
                  {`Search db`}
                </Button>
              )}
            </CommandEmpty>
            <CommandGroup>
              {options?.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const tmpSelectedValues = selectedValues;
                      if (isSelected) {
                        tmpSelectedValues.delete(option.value);
                      } else {
                        tmpSelectedValues.add(option.value);
                      }
                      setSelectedValues(tmpSelectedValues);
                      const filterValues = Array.from(tmpSelectedValues).filter(
                        (a) => !!a,
                      );
                      onFilterChanged?.(filterValues.join(","));
                      // column?.setFilterValue(
                      //   filterValues.length ? filterValues : undefined,
                      // );
                    }}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <CheckIcon className={cn("h-4 w-4")} />
                    </div>
                    {option.icon && (
                      <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    )}
                    <span>{option.label}</span>
                    {facets?.get(option.value) && (
                      <span className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                        {facets.get(option.value)}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && showClearFilters && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onFilterChanged?.()}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
